import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import {
  getMusicOverlay,
  overlayEnded,
  overlayStarted,
  MusicItem,
  MusicOverlay,
} from '../../api/payment/music-request.api';
import {
  decideOverlayAction,
  formatMmSs,
  itemSeconds,
  nextUpItems,
  overlayPollMs,
  overlayResumeOffset,
  planPlayer,
  shouldEndPlayback,
  YT_STATE,
} from '../../utils/music-request';
import { createResponseGate } from '../../utils/response-gate';
import useYoutubePlayer from '../../hooks/use-youtube-player/use-youtube-player';
import usePollWhile from '../../hooks/use-poll-while/use-poll-while';

/**
 * Overlay PHÁT NHẠC THEO YÊU CẦU — chèn vào OBS bằng Browser Source, link có `?key=`.
 *
 * Không đăng nhập: đọc trạng thái hàng chờ qua endpoint công khai (có key), phát bằng trình
 * nhúng YouTube chính thức, báo máy chủ lúc bắt đầu và lúc hết bài. Máy chủ là nguồn sự thật:
 * mỗi lần hàng chờ đổi (duyệt, bỏ qua, tạm dừng) hub đẩy `ReceiveMusicUpdate` thì tải lại
 * trạng thái, kèm tải lại dự phòng mỗi 5 đến 8 giây: máy chủ chỉ giữ quyền phát cho một lớp phủ
 * trong 20 giây kể từ lần gọi gần nhất nên nhịp này cũng là nhịp giữ quyền.
 */

// Giống overlay mục tiêu donate: bóng đổ nhiều lớp để chữ nổi trên mọi nền video.
const OUTLINE =
  '0 0 3px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.85), 0 0 14px rgba(0,0,0,.6)';

// Báo hết bài thất bại (mất mạng) ⇒ chờ chút rồi tải lại trạng thái để báo lại.
const ENDED_RETRY_MS = 3000;
// Không gửi lại `started` cho cùng một bài trong khoảng này (chờ hub/tải lại dự phòng).
const START_COOLDOWN_MS = 5000;

// Mỗi lần mở trang một mã ngẫu nhiên: máy chủ dựa vào đó để chỉ cho một lớp phủ phát.
const newInstanceId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // rơi xuống cách dự phòng
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

// Mã lớp phủ của lần mở trước lưu theo kênh trong sessionStorage: gửi kèm để máy chủ chuyển
// quyền phát sang mã mới ngay khi tải lại trang OBS, khỏi bị từ chối tới 20 giây.
const swapStoredInstanceId = (creatorId: string | undefined, next: string): string | undefined => {
  if (!creatorId) return undefined;
  const k = `cab-music-overlay-instance:${creatorId}`;
  let prev: string | undefined;
  try {
    prev = window.sessionStorage.getItem(k) || undefined;
    window.sessionStorage.setItem(k, next);
  } catch {
    // sessionStorage bị chặn: bỏ qua, quay về chờ hết hạn quyền
  }
  return prev && prev !== next ? prev : undefined;
};

const isForbidden = (err: unknown) =>
  (err as { response?: { status?: number } })?.response?.status === 403;

type EndReason = 'finished' | 'error';

const PageMusicLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key') || '';

  const [overlay, setOverlay] = useState<MusicOverlay | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [nowPlaying, setNowPlaying] = useState<MusicItem | null>(null);

  const instanceIdRef = useRef<string>('');
  if (!instanceIdRef.current) instanceIdRef.current = newInstanceId();
  const instanceId = instanceIdRef.current;
  const previousIdRef = useRef<string | undefined | null>(null);
  if (previousIdRef.current === null) previousIdRef.current = swapStoredInstanceId(contentCreatorId, instanceId);
  const previousInstanceId = previousIdRef.current;
  const receivedAtRef = useRef(0);
  // Bài do chính lớp phủ này vừa báo bắt đầu: phát từ giây 0, không tua theo giờ máy chủ.
  const startedHereRef = useRef<Set<string>>(new Set());

  const hostRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<MusicOverlay | null>(null);
  overlayRef.current = overlay;
  const loadedRef = useRef<MusicItem | null>(null);
  const endedIdsRef = useRef<Set<string>>(new Set());
  const startingIdRef = useRef<string | null>(null);
  const appliedPausedRef = useRef<boolean | null>(null);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  // Mỗi phiên (kênh + key) một gate mới: phản hồi của phiên cũ về muộn thì bỏ qua (gate khác),
  // còn trong cùng phiên thì gate chặn phản hồi về lệch thứ tự (xem utils/response-gate).
  const gateRef = useRef(createResponseGate());

  const active = !!contentCreatorId && !!key && !forbidden;

  const player = useYoutubePlayer(hostRef, {
    enabled: active,
    // Âm lượng streamer đặt (Cài đặt hoặc bảng điều khiển). Hub đẩy ReceiveMusicUpdate khi đổi nên lớp phủ tải lại và
    // áp ngay, tải lại dự phòng 5 đến 8 giây là đường lùi. Chưa tải được dữ liệu ⇒ 100 như trước.
    volume: overlay?.volume,
    onTick: (time, state) => {
      const item = loadedRef.current;
      const o = overlayRef.current;
      if (item && o && shouldEndPlayback(time, itemSeconds(item, o.maxSeconds), state)) finishRef.current(item, 'finished');
    },
    onStateChange: (state) => {
      const item = loadedRef.current;
      if (item && state === YT_STATE.ENDED) finishRef.current(item, 'finished');
    },
    onError: () => {
      const item = loadedRef.current;
      if (item) finishRef.current(item, 'error');
    },
  });
  const playerRef = useRef(player);
  playerRef.current = player;

  const later = (fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timersRef.current.delete(t);
      fn();
    }, ms);
    timersRef.current.add(t);
  };

  const applyOverlay = useCallback((o: MusicOverlay) => {
    receivedAtRef.current = Date.now();
    setOverlay(o);
  }, []);

  const handleApiError = useCallback((err: unknown) => {
    if (isForbidden(err)) {
      setForbidden(true);
      setOverlay(null);
    }
    // Lỗi khác (mạng, máy chủ bận): giữ nguyên, hub hoặc tải lại dự phòng sẽ thử lại.
  }, []);

  const load = useCallback(async () => {
    if (!contentCreatorId || !key) return;
    const gate = gateRef.current;
    const token = gate.issue();
    try {
      const o = await getMusicOverlay(contentCreatorId, key, instanceId, previousInstanceId);
      // Hub, nối lại mạng, tải lại dự phòng, thử lại sau lỗi có thể chồng nhau: chỉ áp lần mới nhất.
      if (gate === gateRef.current && gate.acceptRead(token)) applyOverlay(o);
    } catch (err) {
      if (gate === gateRef.current) handleApiError(err);
    }
  }, [contentCreatorId, key, instanceId, previousInstanceId, handleApiError, applyOverlay]);
  const loadRef = useRef(load);
  loadRef.current = load;

  const releasePlayer = () => {
    loadedRef.current = null;
    // Bài đã dừng: lần phát sau (kể cả lấy lại quyền) tua theo giờ máy chủ, không phát lại từ 0.
    startedHereRef.current = new Set();
    setNowPlaying(null);
  };

  /** Báo máy chủ hết bài — tối đa một lần cho mỗi bài dù cả trần thời gian lẫn ENDED cùng tới. */
  const finish = (item: MusicItem, reason: EndReason) => {
    if (!contentCreatorId || endedIdsRef.current.has(item.id)) return;
    endedIdsRef.current.add(item.id);
    if (loadedRef.current?.id === item.id) {
      playerRef.current.stop();
      releasePlayer();
    }
    const gate = gateRef.current;
    const token = gate.issue();
    overlayEnded(contentCreatorId, key, item.id, reason, instanceId, previousInstanceId)
      .then((o) => {
        if (gate === gateRef.current && gate.acceptWrite(token)) applyOverlay(o);
      })
      .catch((err) => {
        if (gate !== gateRef.current) return;
        if (isForbidden(err)) {
          handleApiError(err);
          return;
        }
        later(() => {
          endedIdsRef.current.delete(item.id);
          loadRef.current();
        }, ENDED_RETRY_MS);
      });
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  // Đổi kênh/key: bỏ hết trạng thái cũ rồi tải mới.
  useEffect(() => {
    gateRef.current = createResponseGate();
    endedIdsRef.current = new Set();
    startingIdRef.current = null;
    startedHereRef.current = new Set();
    appliedPausedRef.current = null;
    loadedRef.current = null;
    setNowPlaying(null);
    setOverlay(null);
    setForbidden(false);
    load();
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [load]);

  // Sai key: player bị huỷ (enabled=false), chỉ cần quên bài đang giữ.
  useEffect(() => {
    if (forbidden) releasePlayer();
  }, [forbidden]);

  // Trạng thái máy chủ đổi ⇒ quyết định việc của player.
  useEffect(() => {
    if (!overlay || !contentCreatorId) return;
    if (overlay.isActiveInstance === false) startedHereRef.current = new Set();
    const p = playerRef.current;
    const plan = planPlayer(
      decideOverlayAction(overlay),
      loadedRef.current?.id ?? null,
      endedIdsRef.current
    );

    switch (plan.kind) {
      case 'load': {
        const cut = itemSeconds(plan.item, overlay.maxSeconds);
        const offset = overlayResumeOffset({
          startedAt: plan.item.startedAt,
          serverNow: overlay.serverNow,
          receivedAtMs: receivedAtRef.current,
          nowMs: Date.now(),
          maxSeconds: cut,
          startedHere: startedHereRef.current.has(plan.item.id),
        });
        loadedRef.current = plan.item;
        setNowPlaying(plan.item);
        if (shouldEndPlayback(offset, cut, YT_STATE.PLAYING)) {
          // Mở lại overlay khi bài đã phát hết thời lượng: báo xong luôn, không phát lại.
          finishRef.current(plan.item, 'finished');
        } else {
          p.play(plan.item.videoId, offset, overlay.isPaused);
        }
        break;
      }
      case 'keep':
        if (overlay.current) setNowPlaying(overlay.current);
        if (appliedPausedRef.current !== null && appliedPausedRef.current !== overlay.isPaused) {
          if (overlay.isPaused) p.pause();
          else p.resume();
        }
        break;
      case 'stop':
        p.stop();
        releasePlayer();
        break;
      case 'start': {
        if (plan.stopFirst) {
          p.stop();
          releasePlayer();
        }
        const { id } = plan.item;
        if (startingIdRef.current === id) break;
        startingIdRef.current = id;
        later(() => {
          if (startingIdRef.current === id) startingIdRef.current = null;
        }, START_COOLDOWN_MS);
        const gate = gateRef.current;
        const token = gate.issue();
        overlayStarted(contentCreatorId, key, id, instanceId, previousInstanceId)
          .then((o) => {
            if (gate === gateRef.current && gate.acceptWrite(token)) {
              if (o.current?.id === id) startedHereRef.current.add(id);
              applyOverlay(o);
            }
          })
          .catch((err) => {
            if (gate === gateRef.current) handleApiError(err);
          });
        break;
      }
      default:
        break;
    }
    appliedPausedRef.current = overlay.isPaused;
    // Chỉ chạy khi trạng thái máy chủ đổi; các hàm phụ đọc qua ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay]);

  // Hub: máy chủ đẩy `ReceiveMusicUpdate` tới kênh mỗi khi hàng chờ đổi.
  useEffect(() => {
    if (!contentCreatorId || !key || forbidden) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    connection.on('ReceiveMusicUpdate', () => loadRef.current());
    // Nối lại sau khi rớt mạng: có thể đã lỡ sự kiện nên tải lại luôn.
    connection.onreconnected(() => loadRef.current());
    connection.start().catch(() => {
      // Không nối được hub: vẫn còn tải lại dự phòng.
    });
    return () => {
      connection.stop();
    };
  }, [contentCreatorId, key, forbidden]);

  // Nhịp này đồng thời là nhịp giữ quyền phát với máy chủ (quyền hết sau 20 giây không gọi).
  usePollWhile(active, () => loadRef.current(), overlayPollMs(overlay));

  // Thiếu key hoặc sai key ⇒ trống hoàn toàn để OBS trong suốt.
  if (!active) return null;

  const maxSeconds = nowPlaying ? itemSeconds(nowPlaying, overlay?.maxSeconds || 0) : overlay?.maxSeconds || 0;
  const limit = player.duration > 0 ? Math.min(player.duration, maxSeconds || player.duration) : maxSeconds;
  const upNext = nowPlaying ? nextUpItems(overlay) : [];
  const elsewhere = overlay?.isActiveInstance === false;
  const percent = limit > 0 ? Math.max(0, Math.min(100, (player.currentTime / limit) * 100)) : 0;

  return (
    <div className="flex min-h-screen w-full items-start justify-center bg-transparent p-4">
      {elsewhere && (
        <div
          className="pointer-events-none fixed bottom-1 left-2 text-[10px] text-white opacity-30"
          style={{ textShadow: OUTLINE }}
        >
          Lớp phủ nhạc đang mở ở nơi khác.
        </div>
      )}
      <div
        className={`w-full max-w-[960px] transition-opacity duration-300 ${
          nowPlaying ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black">
          <div ref={hostRef} className="absolute inset-0 [&>iframe]:h-full [&>iframe]:w-full" />
          {nowPlaying && player.needsUnmute && (
            <button
              type="button"
              onClick={player.unMute}
              className="absolute bottom-3 right-3 rounded-full bg-black/70 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-black/85"
            >
              🔊 Bấm để bật tiếng
            </button>
          )}
        </div>

        {nowPlaying && (
          <div className="mt-3 text-white" style={{ textShadow: OUTLINE }}>
            <div className="truncate text-3xl font-extrabold">{nowPlaying.title}</div>
            <div className="mt-1 truncate text-xl font-bold">
              Yêu cầu bởi {nowPlaying.requesterName}
            </div>
            {nowPlaying.message && (
              <div className="mt-1 line-clamp-2 text-lg font-semibold italic">
                “{nowPlaying.message}”
              </div>
            )}
            <div className="mt-3 flex items-center gap-3">
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-black/40 ring-1 ring-black/30">
                <div
                  className="h-full rounded-full bg-white transition-[width] duration-500 ease-linear"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <span className="shrink-0 text-lg font-bold tabular-nums">
                {formatMmSs(player.currentTime)} / {formatMmSs(limit)}
              </span>
            </div>
            {upNext.length > 0 && (
              <div className="mt-3">
                <div className="text-base font-bold uppercase tracking-wide">Tiếp theo</div>
                <ol className="mt-1 space-y-0.5">
                  {upNext.map((n, i) => (
                    <li key={n.id} className="truncate text-lg font-semibold">
                      {i + 1}. {n.title} - {n.requesterName}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageMusicLivestream;
